import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";
import NewsletterSignup from "@/components/NewsletterSignup";
import { trackCta } from "@/lib/analytics";

const serif = { fontFamily: "'Cormorant Garamond','EB Garamond',Georgia,serif" };

/**
 * NewsletterDisclosure — the site-wide quiet newsletter entry point.
 * Collapsed: title + short intro + one button (mode="section"), or just the
 * button (mode="inline", used where the parent supplies its own copy).
 * Expanded: the existing NewsletterSignup (Kit) form, with focus moved to the
 * e-mail field, ESC/close to collapse, and auto-open on the #newsletter hash.
 */
export default function NewsletterDisclosure({ variant = "global", mode = "section" }) {
  const { t } = useT();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = `newsletter-panel-${variant}`;

  useEffect(() => {
    if (location.hash === "#newsletter") {
      setOpen(true);
      const tm = setTimeout(() => {
        rootRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return () => clearTimeout(tm);
    }
  }, [location]);

  // After opening, move focus to the Kit e-mail field once it has rendered.
  useEffect(() => {
    if (!open) return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = panelRef.current?.querySelector('input[type="email"], input[name="email_address"]');
      tries += 1;
      if (el) { el.focus(); clearInterval(iv); }
      else if (tries > 40) clearInterval(iv);
    }, 150);
    return () => clearInterval(iv);
  }, [open]);

  // ESC closes the panel wherever focus is on the page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        requestAnimationFrame(() => triggerRef.current?.focus());
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const openPanel = () => {
    setOpen(true);
    trackCta("newsletter_open", { placement: variant });
  };
  const closePanel = () => {
    setOpen(false);
    requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const triggerBtn = (
    <button
      type="button"
      ref={triggerRef}
      onClick={openPanel}
      aria-expanded={open}
      aria-controls={panelId}
      data-testid={`newsletter-open-btn-${variant}`}
      className="inline-block min-h-[48px] px-7 py-3.5 bg-gold text-ink text-[11px] tracking-[0.24em] uppercase font-semibold leading-relaxed hover:bg-gold/90 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70"
    >
      {t("newsletter.cta.button")}
    </button>
  );

  const panel = (
    <div
      id={panelId}
      ref={panelRef}
      role="region"
      aria-label={t("newsletter.cta.title")}
      data-testid={`newsletter-panel-${variant}`}
      className="relative"
    >
      <div className="flex justify-end">
        <button
          type="button"
          onClick={closePanel}
          data-testid={`newsletter-close-btn-${variant}`}
          aria-label={t("newsletter.cta.close")}
          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase text-fog hover:text-bone transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/70 px-2 py-1"
        >
          <X size={13} strokeWidth={1.5} aria-hidden="true" />
          {t("newsletter.cta.close")}
        </button>
      </div>
      <NewsletterSignup variant={variant} embedded />
    </div>
  );

  if (mode === "inline") {
    return (
      <div ref={rootRef} data-testid={`newsletter-disclosure-${variant}`}>
        {open ? panel : triggerBtn}
      </div>
    );
  }

  return (
    <section
      ref={rootRef}
      id="newsletter"
      data-testid={`newsletter-disclosure-${variant}`}
      className={variant === "article"
        ? "max-w-3xl mx-auto px-6 md:px-10 mt-20 md:mt-24"
        : "max-w-3xl mx-auto px-6 md:px-10 mt-24 md:mt-32 mb-20 md:mb-24"}
    >
      {open ? panel : (
        <div className="border border-gold/30 bg-[#050505] p-8 md:p-12 text-center">
          <h2 style={serif} className="text-2xl md:text-3xl text-bone">
            {t("newsletter.cta.title")}
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-bone/60 font-light leading-relaxed text-sm md:text-base">
            {t("newsletter.cta.intro")}
          </p>
          <div className="mt-7">{triggerBtn}</div>
        </div>
      )}
    </section>
  );
}
